import { EntityRefBasePicker } from '@agilelab/plugin-wb-platform';
import { Entity, isGroupEntity, isUserEntity } from '@backstage/catalog-model';
import { parseEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useCallback, useEffect, useState, useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { FieldExtensionUiSchema } from '../../../extensions/types';
import { extractCustomProperties, isHidden } from '../../utils';
import { IdentitiesPickerProps, IdentitiesPickerUiOptions } from './schema';
import { isServiceAccountEntity } from '@agilelab/plugin-wb-service-accounts-common';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';
import { useEntitiesMembership } from '../../hooks/useEntitiesMembership';

const ALLOWED_KINDS = ['user', 'group'];

function validateUiOptions(
  uiSchema: FieldExtensionUiSchema<string, IdentitiesPickerUiOptions>,
): Error | undefined {
  if (
    uiSchema['ui:options']?.allowedKinds !== undefined &&
    !uiSchema['ui:options']?.allowedKinds?.every(
      (val: string) =>
        val.toLowerCase() === 'group' || val.toLowerCase() === 'user',
    )
  ) {
    return new Error(
      `This field has been disabled due to a misconfiguration. Please contact the platform team. Option 'allowedKinds' can assume either 'user' or 'group'`,
    );
  }

  if (
    uiSchema['ui:options']?.showOnlyUserMemberGroups !== undefined &&
    !(typeof uiSchema['ui:options']?.showOnlyUserMemberGroups === 'boolean')
  ) {
    return new Error(
      'This field has been disabled due to a misconfiguration. Please contact the platform team. Option `showOnlyUserMember` must be either `true` or `false`.',
    );
  }

  if (
    uiSchema['ui:options']?.maxIdentities !== undefined &&
    !(typeof uiSchema['ui:options']?.maxIdentities === 'number')
  ) {
    return new Error(
      'This field has been disabled due to a misconfiguration. Please contact the platform team. Option `maxIdentities` must be a number.',
    );
  }

  if (
    uiSchema['ui:options']?.excludeGroups !== undefined &&
    !uiSchema['ui:options']?.excludeGroups?.every((value: string) => {
      try {
        const entityRef = parseEntityRef(value);
        return entityRef.kind.toLowerCase() === 'group';
      } catch {
        return false;
      }
    })
  ) {
    return new Error(
      "This field has been disabled due to a misconfiguration. Please contact the platform team. Option 'excludeGroups' must use 'group:name' or 'group:namespace/name' format.",
    );
  }

  if (
    uiSchema['ui:options']?.showOnlyServiceAccounts &&
    uiSchema['ui:options']?.excludeServiceAccounts
  ) {
    return new Error(
      "This field has been disabled due to a misconfiguration. Please contact the platform team. You must enable at most one between 'showOnlyServiceAccounts' and 'excludeServiceAccounts'.",
    );
  }

  return undefined;
}

const allowedKinds = (
  uiSchema: FieldExtensionUiSchema<string, IdentitiesPickerUiOptions>,
): string[] =>
  uiSchema['ui:options']?.allowedKinds?.map((kind: string) =>
    kind.toLowerCase(),
  ) ?? ALLOWED_KINDS;

const fromEntityToString = (entity: Entity) =>
  `${entity.kind.toLowerCase()}:${entity.metadata.name}`;

export const IdentitiesPicker = (props: IdentitiesPickerProps) => {
  const {
    schema,
    uiSchema,
    formContext,
    onChange,
    required,
    disabled,
    idSchema,
    placeholder,
  } = props;

  const catalogApi = useApi(catalogApiRef);
  const [configError, setConfigError] = useState<Error | undefined>(undefined);
  const customProps = extractCustomProperties(uiSchema);

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id);
  }, [idSchema]);

  // Handle changes with dependency manager support
  const handleChange = useCallback(
    (value: any) => {
      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  const { getMembersIdentities, identityLoading } = useEntitiesMembership();

  useEffect(() => {
    const error = validateUiOptions(uiSchema);
    setConfigError(error);
  }, [uiSchema]);

  const normalizeGroupName = (value: string): string => {
    return parseEntityRef(value).name.toLowerCase();
  };

  const excludeGroupsNormalized = (
    uiSchema['ui:options']?.excludeGroups ?? []
  ).map(normalizeGroupName);

  const showOnlyServiceAccounts =
    uiSchema['ui:options']?.showOnlyServiceAccounts;

  const excludeServiceAccounts = uiSchema['ui:options']?.excludeServiceAccounts;

  const { value: identities } = useAsync(async () => {
    if (identityLoading) {
      return [];
    }
    const filter = { kind: allowedKinds(uiSchema) };
    if (uiSchema['ui:options']?.showOnlyUserMemberGroups ?? false) {
      return await getMembersIdentities({
        filter: {
          ...filter,
          excludeGroupNames: excludeGroupsNormalized,
        },
      });
    }

    return (await catalogApi.getEntities({ filter })).items;
  }, [catalogApi, identityLoading]);

  const options = useMemo(() => {
    // define all the conditions entities must satisfy to be included in the final list based on the uiSchema configuration
    const filterFns: ((entity: Entity) => boolean)[] = [];
    if (excludeGroupsNormalized.length > 0)
      filterFns.push(e => {
        if (!isGroupEntity(e)) return true;
        const excluded = excludeGroupsNormalized.includes(
          e.metadata.name.toLowerCase(),
        );
        return !excluded;
      });
    if (excludeServiceAccounts) {
      filterFns.push(e => {
        if (!isUserEntity(e)) return true;
        return !isServiceAccountEntity(e);
      });
    } else if (showOnlyServiceAccounts)
      filterFns.push(e => {
        if (!isUserEntity(e)) return true;
        return isServiceAccountEntity(e);
      });

    return (identities ?? [])
      .filter(identity => {
        // apply all conditions, an entity must satisfy all of them to be included
        return filterFns.every(fn => fn(identity));
      })
      .map(fromEntityToString);
  }, [
    identities,
    excludeGroupsNormalized,
    showOnlyServiceAccounts,
    excludeServiceAccounts,
  ]);

  return (
    <EntityRefBasePicker
      options={options}
      hidden={isHidden(uiSchema)}
      prefillValues={formContext[props.name] ?? []}
      onChange={handleChange}
      maxItems={uiSchema['ui:options']?.maxIdentities ?? undefined}
      required={required}
      disabled={disabled || configError !== undefined}
      title={schema.title ?? ''}
      helperText={
        configError?.message ?? schema.description ?? 'Select the identities'
      }
      error={configError ? true : false}
      additionalErrors={configError ? [configError.message] : undefined}
      placeholder={placeholder}
      fieldId={idSchema.$id}
      customProps={customProps}
    />
  );
};
